import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Highlighter,
  Baseline,
  RemoveFormatting,
} from 'lucide-react';
import { Separator } from '@/shared/components/ui/separator';
import { Button } from '@/shared/components/ui/button';
import { ToolbarButton } from './ToolbarButton';
import { ColorPickerPopover } from './ColorPickerPopover';
import { LinkPopover } from './LinkPopover';

interface BubbleToolbarProps {
  editor: Editor;
}

/**
 * Floating inline toolbar shown on text selection.
 * Mobile: shows core actions only. md+: full toolbar.
 */
export function BubbleToolbar({ editor }: BubbleToolbarProps) {
  const { t } = useTranslation('richText');

  const textColor = (editor.getAttributes('textStyle').color as string | undefined) ?? null;
  const highlightColor = (editor.getAttributes('highlight').color as string | undefined) ?? null;

  return (
    <div
      data-testid="rich-text-bubble-toolbar"
      className="flex items-center gap-1 rounded-xl border border-border/60 bg-popover px-1.5 py-1 shadow-lg ring-1 ring-border/40"
    >
      {/* Core format — always visible */}
      <ToolbarButton
        icon={<Bold className="h-4 w-4" />}
        label={t('bubble.bold')}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        testId="rich-text-bubble-bold"
      />
      <ToolbarButton
        icon={<Italic className="h-4 w-4" />}
        label={t('bubble.italic')}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        testId="rich-text-bubble-italic"
      />

      {/* Extended format — hidden on mobile */}
      <div className="hidden md:flex items-center gap-1">
        <ToolbarButton
          icon={<Underline className="h-4 w-4" />}
          label={t('bubble.underline')}
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          testId="rich-text-bubble-underline"
        />
        <ToolbarButton
          icon={<Strikethrough className="h-4 w-4" />}
          label={t('bubble.strike')}
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          testId="rich-text-bubble-strike"
        />
        <ToolbarButton
          icon={<Code className="h-4 w-4" />}
          label={t('bubble.code')}
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          testId="rich-text-bubble-code"
        />

        <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

        <ColorPickerPopover
          type="text"
          value={textColor}
          onChange={(color) =>
            color
              ? editor.chain().focus().setColor(color).run()
              : editor.chain().focus().unsetColor().run()
          }
          testId="rich-text-color-picker-text"
          trigger={
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={t('bubble.textColor')}
              data-testid="rich-text-bubble-text-color"
              className="relative h-7 w-7 transition-colors duration-100"
            >
              <Baseline className="h-4 w-4" />
              <span
                className="absolute bottom-1 left-1/2 h-[2px] w-3 -translate-x-1/2 rounded-full"
                style={{ backgroundColor: textColor ?? 'transparent' }}
              />
            </Button>
          }
        />

        <ColorPickerPopover
          type="highlight"
          value={highlightColor}
          onChange={(color) =>
            color
              ? editor.chain().focus().setHighlight({ color }).run()
              : editor.chain().focus().unsetHighlight().run()
          }
          testId="rich-text-color-picker-highlight"
          trigger={
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={t('bubble.highlight')}
              data-testid="rich-text-bubble-highlight"
              className="relative h-7 w-7 transition-colors duration-100"
            >
              <Highlighter className="h-4 w-4" />
              <span
                className="absolute bottom-1 left-1/2 h-[2px] w-3 -translate-x-1/2 rounded-full"
                style={{ backgroundColor: highlightColor ?? 'transparent' }}
              />
            </Button>
          }
        />

        <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

        <ToolbarButton
          icon={<RemoveFormatting className="h-4 w-4" />}
          label={t('bubble.clearFormat')}
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          testId="rich-text-bubble-clear-format"
        />
      </div>

      <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

      <LinkPopover editor={editor} />
    </div>
  );
}
